import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactoPage = () => {
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('plan');
  const planPrecio = searchParams.get('precio');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-fill message if coming from membership page
  useEffect(() => {
    if (planName) {
      const membershipInfo = `Hola, estoy interesado/a en la membresía "${planName}"${planPrecio ? ` (${planPrecio})` : ''}. Me gustaría recibir más información.`;
      setFormData(prev => ({ ...prev, message: membershipInfo }));
    }
  }, [planName, planPrecio]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Por favor, completa los campos obligatorios');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSent(true);
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
    } catch (error) {
      toast.error('Error al enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-halo-cream min-h-screen" data-testid="contacto-page">
      {/* Hero */}
      <section className="py-16 bg-halo-charcoal">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl text-halo-cream mb-4">
              Contacto
            </h1>
            <p className="text-halo-cream/70 max-w-xl mx-auto">
              ¿Tienes alguna pregunta? Estamos aquí para ayudarte.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-3xl text-halo-charcoal mb-4">
                  Visítanos en HALO
                </h2>
                <p className="text-halo-text leading-relaxed">
                  Te esperamos en nuestro espacio de bienestar. No dudes en 
                  contactarnos para cualquier consulta sobre nuestros servicios, 
                  membresías o disponibilidad.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-halo-brown/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={22} className="text-halo-brown" />
                  </div>
                  <div>
                    <h3 className="font-medium text-halo-charcoal mb-1">Dirección</h3>
                    <p className="text-halo-text">
                      Calle Miquel Caplloch 6, Local 12<br />
                      Palma de Mallorca
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-halo-brown/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={22} className="text-halo-brown" />
                  </div>
                  <div>
                    <h3 className="font-medium text-halo-charcoal mb-1">Teléfono</h3>
                    <a 
                      href="tel:678901086" 
                      className="text-halo-text hover:text-halo-brown transition-colors"
                      data-testid="contact-phone"
                    >
                      678 901 086
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-halo-brown/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={22} className="text-halo-brown" />
                  </div>
                  <div>
                    <h3 className="font-medium text-halo-charcoal mb-1">Email</h3>
                    <a 
                      href="mailto:info@halocuevas.com" 
                      className="text-halo-text hover:text-halo-brown transition-colors"
                      data-testid="contact-email"
                    >
                      info@halocuevas.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-halo-brown/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={22} className="text-halo-brown" />
                  </div>
                  <div>
                    <h3 className="font-medium text-halo-charcoal mb-1">Horario</h3>
                    <p className="text-halo-text">
                      Lunes - Viernes: 10:00 - 20:00<br />
                      Sábados: 10:00 - 14:00<br />
                      Domingos: Cerrado
                    </p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="bg-halo-surface rounded-2xl h-[250px] border border-halo-taupe/10 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.9!2d2.1!3d41.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDI0JzAwLjAiTiAywrAwNicwMC4wIkU!5e0!3m2!1ses!2ses!4v1600000000000!5m2!1ses!2ses"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación HALO"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {sent ? (
                <div className="bg-halo-surface rounded-2xl p-12 border border-halo-taupe/10 text-center">
                  <div className="w-16 h-16 bg-halo-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={32} className="text-halo-sage" />
                  </div>
                  <h3 className="font-heading text-2xl text-halo-charcoal mb-4">
                    ¡Mensaje Enviado!
                  </h3>
                  <p className="text-halo-text mb-6">
                    Gracias por contactarnos. Te responderemos lo antes posible.
                  </p>
                  <Button
                    onClick={() => {
                      setSent(false);
                      setFormData({ name: '', email: '', phone: '', message: '' });
                    }}
                    variant="outline"
                    className="border-halo-brown text-halo-brown hover:bg-halo-brown hover:text-halo-cream"
                  >
                    Enviar otro mensaje
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10">
                  <h3 className="font-heading text-2xl text-halo-charcoal mb-6">
                    Envíanos un mensaje
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="contact-name">Nombre *</Label>
                      <Input
                        id="contact-name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Tu nombre"
                        className="mt-2"
                        data-testid="contact-input-name"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="tu@email.com"
                          className="mt-2"
                          data-testid="contact-input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-phone">Teléfono</Label>
                        <Input
                          id="contact-phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="678 901 086"
                          className="mt-2"
                          data-testid="contact-input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-message">Mensaje *</Label>
                      <Textarea
                        id="contact-message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="¿En qué podemos ayudarte?"
                        className="mt-2"
                        rows={5}
                        data-testid="contact-input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-halo-brown text-halo-cream hover:bg-halo-charcoal disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {sending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-halo-cream border-t-transparent mr-2" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar Mensaje
                          <Send size={18} className="ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-halo-text text-center">
                      Al enviar este formulario, aceptas nuestra política de privacidad.
                    </p>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactoPage;
